import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserProfile {
  display_name: string;
}

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('user_profiles')
      .select('display_name')
      .eq('user_id', user.id)
      .single();

    setProfile(data ? { display_name: data.display_name } : null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const saveDisplayName = useCallback(async (displayName: string) => {
    if (!user) return { error: 'Nie zalogowany' };

    const { error } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: user.id, display_name: displayName },
        { onConflict: 'user_id' }
      );

    if (!error) {
      setProfile({ display_name: displayName });
    }

    return { error: error?.message || null };
  }, [user]);

  return {
    profile,
    loading,
    displayName: profile?.display_name || '',
    saveDisplayName,
    reload: loadProfile,
  };
};

/**
 * Hook to resolve user_ids to display names.
 * Loads all user_profiles and returns a lookup map.
 */
export const useDisplayNames = () => {
  const [namesMap, setNamesMap] = useState<Record<string, string>>({});
  const { user } = useAuth();

  const loadNames = useCallback(async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('user_id, display_name');

    if (data) {
      const map: Record<string, string> = {};
      for (const row of data) {
        if (row.display_name) {
          map[row.user_id] = row.display_name;
        }
      }
      setNamesMap(map);
    }
  }, []);

  useEffect(() => {
    loadNames();
  }, [loadNames]);

  const getDisplayName = useCallback((userId: string): string => {
    if (namesMap[userId]) return namesMap[userId];
    if (userId === user?.id) return 'Ty';
    return 'Uzytkownik';
  }, [namesMap, user?.id]);

  const getInitial = useCallback((userId: string): string => {
    const name = namesMap[userId];
    if (name) return name.charAt(0).toUpperCase();
    if (userId === user?.id) return 'Ty';
    return 'U';
  }, [namesMap, user?.id]);

  return { namesMap, getDisplayName, getInitial, reload: loadNames };
};
